import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import apiService from '../../services/api';
import EmptyState from '../UI/EmptyState';
import SkeletonLoader from '../UI/SkeletonLoader';
import styles from '../../styles/NotificationsPage.module.css';

// Notification types
interface NotificationItem {
  id: string;
  type: string; // All notification types from backend
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  actionLabel?: string;
  actionUrl?: string;
  actionType?: string;
  metadata?: {
    taskId?: string;
    groupId?: string;
    badgeId?: string;
    points?: number;
    taskName?: string;
    groupName?: string;
    senderName?: string;
    senderAvatar?: string;
  };
}

interface TaskSummary {
  id: string;
  title: string;
  group: string;
  dueDate?: string;
  priority: 'high' | 'medium' | 'low';
}

interface ApprovalSummary {
  id: string;
  title: string;
  submittedBy: string;
  submittedAt: string;
}

type FilterType = 'all' | 'tasks' | 'comments' | 'achievements';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [pendingTasks, setPendingTasks] = useState<TaskSummary[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalSummary[]>([]);
  const [userStats, setUserStats] = useState<{
    total_points: number;
    badges_count: number;
    completed_tasks: number;
    in_progress_tasks: number;
    on_time_percentage: number;
  } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load notifications from backend
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    showLoader('Loading notifications...');

    try {
      // Load notifications
      const notificationsResponse = await apiService.getNotifications({
        page: 1,
        page_size: 50
      });

      if (notificationsResponse.status === 'success' && notificationsResponse.data) {
        const notificationsData = notificationsResponse.data.notifications || [];
        const mappedNotifications: NotificationItem[] = notificationsData.map((notif: any) => ({
          id: notif.id,
          type: notif.type as NotificationItem['type'],
          title: notif.title || '',
          message: notif.message || notif.short_message || '',
          timestamp: notif.timestamp,
          isRead: notif.is_read,
          actionLabel: notif.action_label,
          actionUrl: notif.action_url,
          actionType: notif.action_url ? 'view_task' : undefined,
          metadata: {
            taskId: notif.metadata?.task_id,
            groupId: notif.group?.id,
            badgeId: notif.metadata?.badge_id,
            points: notif.metadata?.points,
            taskName: notif.metadata?.task_name,
            groupName: notif.group?.name,
            senderName: notif.sender?.name,
            senderAvatar: notif.sender?.avatar,
          }
        }));

        setNotifications(mappedNotifications);
      }

      // Load summary data (pending tasks, approvals, stats)
      const summaryResponse = await apiService.getNotificationSummary();

      if (summaryResponse.status === 'success' && summaryResponse.data) {
        const summary = summaryResponse.data;

        // Map pending tasks
        const tasks: TaskSummary[] = (summary.pending_tasks || []).map((task: any) => ({
          id: task.id,
          title: task.title,
          group: task.group,
          dueDate: task.due_date,
          priority: task.priority as 'high' | 'medium' | 'low'
        }));
        setPendingTasks(tasks);

        // Map pending approvals
        const approvals: ApprovalSummary[] = (summary.pending_approvals || []).map((approval: any) => ({
          id: approval.id,
          title: approval.title,
          submittedBy: approval.submitted_by,
          submittedAt: approval.submitted_at
        }));
        setPendingApprovals(approvals);

        // Set user stats
        if (summary.user_stats) {
          setUserStats(summary.user_stats);
        }
      }
    } catch (error: any) {
      console.error('Error loading notifications:', error);
      notificationService.error('Failed to load notifications');
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [showLoader, hideLoader, notificationService]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await loadNotifications();
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    switch (activeFilter) {
      case 'tasks':
        return [
          'task_assigned', 'task_completed', 'task_approved', 'task_rejected',
          'task_comment', 'task_comment_reply', 'task_submission_required',
          'task_submission_reviewed', 'approval_requested', 'approval_granted',
          'approval_denied', 'task_reminder', 'task_due_soon', 'task_overdue'
        ].includes(notification.type);
      case 'comments':
        return [
          'task_comment', 'task_comment_reply', 'group_announcement'
        ].includes(notification.type);
      case 'achievements':
        return [
          'badge_earned', 'badge_milestone', 'points_earned',
          'bonus_points_awarded', 'points_milestone'
        ].includes(notification.type);
      default:
        return true;
    }
  });

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m`;
    } else if (hours < 24) {
      return `${hours}h`;
    } else if (days < 7) {
      return `${days}d`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Get avatar/icon for notification
  const getNotificationAvatar = (notification: NotificationItem) => {
    const initials = notification.metadata?.senderName
      ? notification.metadata.senderName.split(' ').map(n => n[0]).join('').substring(0, 2)
      : notification.metadata?.groupName
        ? notification.metadata.groupName.substring(0, 2)
        : 'WW';

    return (
      <div className={`${styles.avatar} ${getAvatarColorClass(notification.type)}`}>
        {initials.toUpperCase()}
      </div>
    );
  };

  // Get avatar color based on type
  const getAvatarColorClass = (type: NotificationItem['type']) => {
    switch (type) {
      case 'task_assigned':
      case 'task_completed':
        return styles.avatarPrimary;
      case 'comment':
      case 'mention':
        return styles.avatarPurple;
      case 'message':
      case 'group_message':
        return styles.avatarBlue;
      case 'system':
      case 'report':
        return styles.avatarOrange;
      default:
        return styles.avatarGray;
    }
  };

  // Render message with bold text
  const renderMessage = (message: string) => {
    const parts = message.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Handle mark as read
  const handleMarkAsRead = async (id: string) => {
    try {
      await apiService.markNotificationAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      notificationService.error('Failed to mark notification as read');
    }
  };

  // Helper to handle navigation
  const handleNavigation = (url: string) => {
    try {
      let targetUrl = url;

      // Handle legacy /tasks/ URL pattern
      if (targetUrl.includes('/tasks/')) {
        targetUrl = targetUrl.replace('/tasks/', '/task-management/');
      }

      // Check if it's a full URL
      if (targetUrl.startsWith('http')) {
        const urlObj = new URL(targetUrl);
        if (urlObj.origin === window.location.origin) {
          navigate(urlObj.pathname + urlObj.search + urlObj.hash);
        } else {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        navigate(targetUrl);
      }
    } catch (e) {
      console.error('Invalid URL:', url);
      if (url.startsWith('/')) {
        navigate(url);
      }
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    if (notification.actionUrl) {
      handleNavigation(notification.actionUrl);
    } else if (notification.metadata?.taskId) {
      navigate(`/task-management/${notification.metadata.taskId}`);
    } else if (notification.actionType === 'view_task') {
      navigate('/task-management');
    } else if (notification.actionType === 'view_report') {
      navigate('/reports');
    }
  };

  // Handle action click
  const handleActionClick = (e: React.MouseEvent, notification: NotificationItem) => {
    e.stopPropagation();
    handleMarkAsRead(notification.id);

    if (notification.actionUrl) {
      handleNavigation(notification.actionUrl);
      return;
    }

    switch (notification.actionType) {
      case 'view_task':
        if (notification.metadata?.taskId) {
          navigate(`/task-management/${notification.metadata.taskId}`);
        } else {
          navigate('/task-management');
        }
        break;
      case 'view_report':
        navigate('/reports');
        break;
      default:
        break;
    }
  };

  // Toggle menu
  const toggleMenu = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === id ? null : id);
  };

  // Handle delete notification
  const handleDeleteNotification = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await apiService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setOpenMenuId(null);
      notificationService.success('Notification deleted');
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      notificationService.error('Failed to delete notification');
    }
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  // Get priority class
  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'high': return styles.priorityHigh;
      case 'medium': return styles.priorityMedium;
      case 'low': return styles.priorityLow;
      default: return '';
    }
  };

  return (
    <div className={styles.pageContainer}>
      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Left Sidebar */}
        <aside className={styles.leftSidebar}>
          <div className={styles.profileCard}>
            <div className={styles.profileBanner}></div>
            <div className={styles.profileInfo}>
              <div className={styles.profileAvatar}>
                {getUserInitials()}
              </div>
              <h3 className={styles.profileName}>
                {user?.first_name} {user?.last_name}
              </h3>
              <p className={styles.profileRole} style={{ textTransform: 'capitalize' }}>
                {user?.designation || 'Team Member'}
              </p>

              {/* Stats Row */}
              <div className={styles.profileStats}>
                <div className={styles.profileStatItem}>
                  <div className={styles.profileStatIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                  </div>
                  <div className={styles.profileStatContent}>
                    <span className={styles.profileStatValue}>
                      {userStats ? userStats.total_points.toLocaleString() : '0'}
                    </span>
                    <span className={styles.profileStatLabel}>Total Points</span>
                  </div>
                </div>
                <div className={styles.profileStatItem}>
                  <div className={styles.profileStatIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="7"></circle>
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                    </svg>
                  </div>
                  <div className={styles.profileStatContent}>
                    <span className={styles.profileStatValue}>
                      {userStats ? userStats.badges_count : '0'}
                    </span>
                    <span className={styles.profileStatLabel}>Badges Earned</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.settingsCard}>
            <h4 className={styles.settingsTitle}>Manage your notifications</h4>
          </div>
        </aside>

        {/* Center Content */}
        <main className={styles.centerContent}>
          {/* Filter Tabs */}
          <div className={styles.filterTabs}>
            <button
              className={`${styles.filterTab} ${activeFilter === 'all' ? styles.filterTabActive : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All
            </button>
            <button
              className={`${styles.filterTab} ${activeFilter === 'tasks' ? styles.filterTabActive : ''}`}
              onClick={() => setActiveFilter('tasks')}
            >
              Tasks
            </button>
            <button
              className={`${styles.filterTab} ${activeFilter === 'comments' ? styles.filterTabActive : ''}`}
              onClick={() => setActiveFilter('comments')}
            >
              Comments
            </button>
            <button
              className={`${styles.filterTab} ${activeFilter === 'achievements' ? styles.filterTabActive : ''}`}
              onClick={() => setActiveFilter('achievements')}
            >
              Achievements
            </button>
          </div>

          {/* Notifications List */}
          {loading ? (
            <div className={styles.loadingContainer}>
              <SkeletonLoader variant="list" count={6} />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className={styles.emptyContainer}>
              <EmptyState
                image="/images/nonotifications.png"
                title="No Notifications"
                description="You don't have any notifications yet. Check back later for updates."
              />
            </div>
          ) : (
            <div className={styles.notificationsList}>
              {filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {/* Unread Indicator */}
                  <div className={styles.unreadIndicator}>
                    {!notification.isRead && <span className={styles.unreadDot}></span>}
                  </div>

                  {/* Avatar */}
                  {getNotificationAvatar(notification)}

                  {/* Content */}
                  <div className={styles.notificationContent}>
                    <p className={styles.notificationMessage}>
                      {renderMessage(notification.message)}
                    </p>

                    {notification.actionLabel && (
                      <button
                        className={styles.actionButton}
                        onClick={(e) => handleActionClick(e, notification)}
                      >
                        {notification.actionLabel}
                      </button>
                    )}
                  </div>

                  {/* Right Section */}
                  <div className={styles.rightSection}>
                    <span className={styles.timestamp}>
                      {formatTimestamp(notification.timestamp)}
                    </span>
                    <div className={styles.menuWrapper} ref={openMenuId === notification.id ? menuRef : null}>
                      <button
                        className={`${styles.menuButton} ${openMenuId === notification.id ? styles.menuButtonActive : ''}`}
                        onClick={(e) => toggleMenu(e, notification.id)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="5" r="2"></circle>
                          <circle cx="12" cy="12" r="2"></circle>
                          <circle cx="12" cy="19" r="2"></circle>
                        </svg>
                      </button>

                      {openMenuId === notification.id && (
                        <div className={styles.menuDropdown}>
                          <button
                            className={styles.menuDropdownItem}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.id);
                              setOpenMenuId(null);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 11 12 14 22 4"></polyline>
                              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                            </svg>
                            Mark as read
                          </button>
                          <button
                            className={`${styles.menuDropdownItem} ${styles.menuDropdownItemDanger}`}
                            onClick={(e) => handleDeleteNotification(e, notification.id)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Right Sidebar */}
        <aside className={styles.rightSidebar}>
          {/* Pending Tasks Card */}
          <div className={styles.sidebarCard}>
            <div className={styles.sidebarCardHeader}>
              <h4 className={styles.sidebarCardTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                Pending Tasks
              </h4>
              <span className={styles.sidebarBadge}>{pendingTasks.length}</span>
            </div>

            <div className={styles.sidebarCardContent}>
              {pendingTasks.length === 0 ? (
                <p className={styles.emptyText}>No pending tasks</p>
              ) : (
                <ul className={styles.taskList}>
                  {pendingTasks.map(task => (
                    <li key={task.id} className={styles.taskItem} onClick={() => navigate('/task-management')}>
                      <div className={styles.taskInfo}>
                        <span className={`${styles.priorityDot} ${getPriorityClass(task.priority)}`}></span>
                        <div className={styles.taskDetails}>
                          <span className={styles.taskTitle}>{task.title}</span>
                          <span className={styles.taskMeta}>{task.group}{task.dueDate && ` • ${task.dueDate}`}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button className={styles.sidebarViewAll} onClick={() => navigate('/task-management')}>
              View all tasks
            </button>
          </div>

          {/* Pending Approvals Card */}
          <div className={styles.sidebarCard}>
            <div className={styles.sidebarCardHeader}>
              <h4 className={styles.sidebarCardTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Pending Approvals
              </h4>
              <span className={styles.sidebarBadge}>{pendingApprovals.length}</span>
            </div>

            <div className={styles.sidebarCardContent}>
              {pendingApprovals.length === 0 ? (
                <p className={styles.emptyText}>No pending approvals</p>
              ) : (
                <ul className={styles.approvalList}>
                  {pendingApprovals.map(approval => (
                    <li key={approval.id} className={styles.approvalItem} onClick={() => navigate('/task-management')}>
                      <div className={styles.approvalInfo}>
                        <span className={styles.approvalTitle}>{approval.title}</span>
                        <span className={styles.approvalMeta}>by {approval.submittedBy} • {approval.submittedAt}</span>
                      </div>
                      <svg className={styles.approvalArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button className={styles.sidebarViewAll} onClick={() => navigate('/task-management')}>
              View all approvals
            </button>
          </div>

          {/* Quick Stats */}
          <div className={styles.quickStatsCard}>
            <h4 className={styles.quickStatsTitle}>Your Activity</h4>
            <div className={styles.quickStatsGrid}>
              <div className={styles.quickStatItem}>
                <span className={styles.quickStatValue}>
                  {userStats ? userStats.completed_tasks : '0'}
                </span>
                <span className={styles.quickStatLabel}>Completed</span>
              </div>
              <div className={styles.quickStatItem}>
                <span className={styles.quickStatValue}>
                  {userStats ? userStats.in_progress_tasks : '0'}
                </span>
                <span className={styles.quickStatLabel}>In Progress</span>
              </div>
              <div className={styles.quickStatItem}>
                <span className={styles.quickStatValue}>
                  {userStats ? `${userStats.on_time_percentage}%` : '0%'}
                </span>
                <span className={styles.quickStatLabel}>On Time</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default NotificationsPage;
